// "Does this property group contain a modified property?" — the model side of
// the Property Editor's accordion indicators.
//
// It introduces NO new definition of "modified": style properties are asked
// through node.hasExplicitStyle() (the same call PropertyRow's indicator makes)
// and top-level properties through objectPropertyStatus.js. Nothing here ever
// compares a value against a default, and nothing here resets anything — the
// accordion indicator is informational only.
import { isObjectPropertyModified } from './objectPropertyStatus.js';

/**
 * The properties of a part the Property Editor actually renders — and can
 * therefore report as modified. Mirrors PartPropertiesPanel's own filtering:
 * `internal` properties are never controls, and `only`/`exclude` are the same
 * lists the panel passes when a part is split over several accordions.
 */
export function visiblePartProperties(node, part, { only = null, exclude = null } = {}) {
    const properties = node?.getStyleDefinition?.(part)?.properties ?? {};
    return Object.entries(properties)
        .filter(([, meta]) => !meta?.internal)
        .map(([name]) => name)
        .filter(name => (!only || only.includes(name)) && (!exclude || !exclude.includes(name)));
}

/** True when any rendered property of this part carries an explicit override in the given state. */
export function isPartModified(node, part, { state = 'default', only = null, exclude = null } = {}) {
    if (!node?.hasExplicitStyle) return false;
    return visiblePartProperties(node, part, { only, exclude })
        .some(prop => node.hasExplicitStyle(part, prop, state));
}

/** True when any part carries an explicit override in this one state — one state accordion's status. */
export function isStateModified(node, state) {
    return (node?.getPartNames?.() ?? []).some(part => isPartModified(node, part, { state }));
}

/** True when any of a list of top-level (non-style) properties is modified — see objectPropertyStatus.js. */
export function areObjectPropertiesModified(node, props) {
    return (props ?? []).some(prop => isObjectPropertyModified(node, prop));
}

/**
 * Every top-level property this widget declares. Properties without a
 * default/unset state (geometry, identity, designer-only flags) can never be
 * "modified" — isObjectPropertyModified() already rules them out, so having a
 * value is never enough to light up a group.
 */
export function areTopLevelPropertiesModified(node, { exclude = null } = {}) {
    const props = Array.from(node?.schemaPropertyKeys ?? [])
        .filter(prop => !exclude || !exclude.includes(prop));
    return areObjectPropertiesModified(node, props);
}

/**
 * Fold a group tree: a group is modified when it is modified itself or any
 * descendant is. Groups are plain descriptors — `{ modified, children }` — so
 * an extra nesting level later needs no new logic here.
 */
export function isGroupModified(group) {
    if (!group) return false;
    if (group.modified) return true;
    return (group.children ?? []).some(isGroupModified);
}

/**
 * The States accordion as a group tree: one child per supported state, the
 * root folding all of them. StatesPanel renders exactly this — root indicator
 * from isGroupModified(tree), per-state indicator from each child's `modified`.
 */
export function stateGroupTree(node) {
    const children = (node?.getSupportedStates?.() ?? []).map(state => ({
        id: state,
        modified: isStateModified(node, state),
    }));
    return { id: 'states', children };
}
